import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import {
  getInviteInfo,
  publicJoin,
  type InviteInfo,
  type JoinResponse,
} from "../lib/public";
import { Button, Card, Input } from "../components/ui";
import { navigate } from "../lib/router";
import { HelpBox, InfoTip } from "../components/InfoTip";

type Props = {
  onJoined: (response: JoinResponse) => void;
};

export function JoinPage({ onJoined }: Props) {
  // Einladung aus URL: /join/vorname-nachname (Team-Link) oder ?ref=<code>
  const params = new URLSearchParams(window.location.search);
  const slugMatch = window.location.pathname.match(/^\/join\/([^/]+)\/?$/);
  const inviteFromUrl =
    (slugMatch ? decodeURIComponent(slugMatch[1]) : "") || params.get("ref") || "";

  const [inviteCode, setInviteCode] = useState(inviteFromUrl);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [acceptTos, setAcceptTos] = useState(false);
  const [success, setSuccess] = useState<JoinResponse | null>(null);

  // Wer lädt ein? — bei gültigem Code zeigen wir den Namen statt des Feldes.
  const trimmedInvite = inviteCode.trim();
  const inviteInfo = useQuery<InviteInfo>({
    queryKey: ["invite-info", trimmedInvite.toLowerCase()],
    queryFn: () => getInviteInfo(trimmedInvite),
    enabled: trimmedInvite.length >= 3,
    staleTime: 60_000,
    retry: 0,
  });
  // Kam der Code aus dem Link UND ist gültig → Feld komplett verstecken
  const invitePrefilled =
    !!inviteFromUrl && inviteCode === inviteFromUrl && inviteInfo.data?.valid === true;

  const join = useMutation({
    mutationFn: () =>
      publicJoin({
        invite_code: inviteCode.trim(),
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || undefined,
        // Kein eigenes Code-Feld mehr — Backend generiert vorname-nachname
        accept_tos: acceptTos,
      }),
    onSuccess: (r) => setSuccess(r),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    join.mutate();
  }

  if (success) {
    return <JoinSuccess response={success} onContinue={() => onJoined(success)} />;
  }

  return (
    <main className="min-h-screen gradient-radial-warm grid place-items-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-8 text-center">
          <img src="/logo.png" alt="DP Connect" className="mx-auto mb-4 size-16" />
          <h1 className="text-2xl font-semibold tracking-tight">Agent werden</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Werde Empfehlungs-Agent für <span className="brand-text-gradient font-medium">dpconnect.de</span>
          </p>
        </div>

        <div className="mb-4">
          <HelpBox kind="info" collapsible title="ℹ️ Was ist DP Connect Agent?">
            Du bewirbst dich um eine Aufnahme als{" "}
            <strong>Empfehlungs-Agent</strong> für den DP Connect
            Großhandel. Du kannst sofort starten: Kunden werben, Provisionen
            verdienen. Für die Auszahlung hinterlegst du später einfach
            deine Bankdaten (IBAN/USt-ID) im Portal.
            <br />
            <br />
            <strong>Kosten:</strong> keine. <strong>Mindestumsatz:</strong>{" "}
            keiner. Du verdienst nur an Verkäufen die du anstößt.
          </HelpBox>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            {invitePrefilled ? (
              /* Einladung erkannt → kein Code-Feld, nur Bestätigung */
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                <Check className="size-4 shrink-0 text-emerald-700" />
                <p className="text-sm text-emerald-900">
                  Du wurdest von{" "}
                  <strong>{inviteInfo.data?.agent_name}</strong> eingeladen.
                </p>
              </div>
            ) : (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted flex items-center gap-1.5">
                  Einladungs-Code *
                  <InfoTip title="Woher bekomme ich den?">
                    Am einfachsten: Lass dir vom Agenten seinen{" "}
                    <strong>Einladungs-Link</strong> schicken und klick ihn an —
                    dann entfällt dieses Feld komplett. Alternativ trägst du hier
                    seinen Code ein. Ohne Einladung keine Registrierung — das
                    System ist Invite-only.
                  </InfoTip>
                </label>
                <Input
                  required
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="Code aus der Einladung"
                />
                {trimmedInvite.length >= 3 && inviteInfo.data?.valid && (
                  <p className="mt-1 text-xs text-emerald-700 flex items-center gap-1">
                    <Check className="size-3.5" /> Einladung von{" "}
                    <strong>{inviteInfo.data.agent_name}</strong>
                  </p>
                )}
                {trimmedInvite.length >= 3 && inviteInfo.data?.valid === false && (
                  <p className="mt-1 text-xs text-rose-700">
                    Code unbekannt oder inaktiv — frag deinen Agenten nach dem
                    aktuellen Einladungs-Link.
                  </p>
                )}
                {!inviteCode && (
                  <p className="mt-1 text-xs text-ink-muted">
                    Tipp: Mit dem Einladungs-Link deines Agenten ist dieses Feld
                    schon ausgefüllt.
                  </p>
                )}
              </div>
            )}

            <hr className="border-line" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Vorname *
                </label>
                <Input required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Nachname *
                </label>
                <Input required value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Email *
              </label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Telefon (optional)
              </label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                required
                checked={acceptTos}
                onChange={(e) => setAcceptTos(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-xs">
                Ich akzeptiere die{" "}
                <a href="/agb" className="text-accent-orange underline">Nutzungsbedingungen</a>{" "}
                und{" "}
                <a href="/datenschutz" className="text-accent-orange underline">Datenschutz</a>.
                Ich starte als Team-Mitglied (Provision auf eigene
                Verkäufe). Das Hinterlegen von
                IBAN/USt-Daten ist später jederzeit möglich.
              </span>
            </label>

            {join.error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                {join.error instanceof Error ? join.error.message : "Fehler"}
              </div>
            )}

            <Button
              type="submit"
              disabled={join.isPending || !acceptTos}
              className="w-full"
            >
              {join.isPending ? "Erstelle Account…" : "Agent werden"}
            </Button>
          </form>
        </Card>

        <p className="mt-6 text-center text-xs text-ink-muted">
          Schon ein Konto?{" "}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
            className="text-accent-orange hover:underline"
          >
            Login
          </a>
        </p>
      </div>
    </main>
  );
}

function JoinSuccess({
  response,
  onContinue,
}: {
  response: JoinResponse;
  onContinue: () => void;
}) {
  const [copied, setCopied] = useState(false);

  function copyPassword() {
    navigator.clipboard.writeText(response.plaintext_password).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <main className="min-h-screen gradient-radial-warm grid place-items-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-6 text-center">
          <img src="/logo.png" alt="DP Connect" className="mx-auto mb-2 size-12" />
          <div className="mx-auto mb-3 grid size-10 place-items-center rounded-full bg-emerald-100 text-emerald-900 font-bold">
            ✓
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Du bist drin!</h1>
        </div>

        <Card className="space-y-4">
          <p className="text-sm">{response.welcome_note}</p>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-amber-900 mb-2">
              Dein Initial-Passwort — bitte JETZT kopieren
            </div>
            <div className="flex items-center gap-2">
              <code className="font-mono text-sm bg-white px-3 py-1.5 rounded-lg flex-1 select-all">
                {response.plaintext_password}
              </code>
              <Button variant="secondary" onClick={copyPassword}>
                {copied ? "Kopiert!" : "Kopieren"}
              </Button>
            </div>
            <p className="mt-2 text-xs text-amber-800">
              Dieses Passwort wird nicht mehr angezeigt. Speichere es in deinem Passwort-Manager.
              Du kannst es später unter "Konto" ändern.
            </p>
          </div>

          <div className="space-y-1 text-sm">
            <div>
              <span className="text-ink-muted">Email:</span>{" "}
              <code className="font-mono">{response.user.email}</code>
            </div>
            <div>
              <span className="text-ink-muted">Dein persönlicher Link-Name:</span>{" "}
              <code className="font-mono font-semibold">{response.user.agent_code}</code>
            </div>
            <p className="text-xs text-ink-muted">
              Deine Empfehlungs-Links und dein Shop laufen unter diesem Namen —
              alles Weitere findest du im Dashboard unter „Empfehlungs-Links".
            </p>
          </div>

          <Button onClick={onContinue} className="w-full">
            Weiter zum Dashboard
          </Button>
        </Card>
      </div>
    </main>
  );
}
